import { tests } from './tests/index.ts';
import { compareRawData } from './rawDataComparison.ts';
import {
  browserIdentity,
  currentHostParams,
  profileNameFromChromeArgs,
  rawDataEqual,
  testMustMatchControl,
} from './runtime.ts';
import type {
  Profile,
  ProfileResults,
  ProfileSaveRequest,
  RawData,
  ResultsDocument,
  StoredProfile,
  StoredProfileResults,
  TestDefinition,
  TestCategory,
  TestResult,
  WindowRawData,
} from './types.ts';

interface AppState {
  updatedAt: string | null;
  profiles: Record<Profile, ProfileResults | null>;
}

const storedProfiles = [
  'A',
  'B',
  'C',
] as const satisfies readonly StoredProfile[];
const profiles = [...storedProfiles, 'D'] as const satisfies readonly Profile[];

function emptyState(): AppState {
  return {
    updatedAt: null,
    profiles: { A: null, B: null, C: null, D: null },
  };
}

function emptyWindowRawData(): WindowRawData {
  return { A: {}, B: {}, C: {}, D: {} };
}

function profileRawData(
  profile: ProfileResults | null,
): Record<string, RawData> {
  return Object.fromEntries(
    Object.entries(profile?.results ?? {}).map(([testId, testResult]) => [
      testId,
      testResult.rawData,
    ]),
  );
}

const statusElement = requiredElement<HTMLElement>('#status');
const progressElement = requiredElement<HTMLElement>('#test-progress');
const progressBar = requiredElement<HTMLProgressElement>('#test-progress-bar');
const progressName = requiredElement<HTMLElement>('#current-test-name');
const progressCount = requiredElement<HTMLElement>('#test-progress-count');
const rawDataDialog = requiredElement<HTMLDialogElement>('#raw-data-dialog');
const rawDataDialogTitle = requiredElement<HTMLElement>(
  '#raw-data-dialog-title',
);
const rawDataDialogContent = requiredElement<HTMLElement>(
  '#raw-data-dialog-content',
);
const rawDataDialogError = requiredElement<HTMLElement>(
  '#raw-data-dialog-error',
);
const rawDataDialogClose = requiredElement<HTMLButtonElement>(
  '#raw-data-dialog-close',
);
const rawDataDialogCopy = requiredElement<HTMLButtonElement>(
  '#raw-data-dialog-copy',
);
const rawDataDiffDialog = requiredElement<HTMLDialogElement>(
  '#raw-data-diff-dialog',
);
const rawDataDiffDialogTitle = requiredElement<HTMLElement>(
  '#raw-data-diff-dialog-title',
);
const rawDataDiffDialogContent = requiredElement<HTMLElement>(
  '#raw-data-diff-dialog-content',
);
const rawDataDiffDialogClose = requiredElement<HTMLButtonElement>(
  '#raw-data-diff-dialog-close',
);
const sharedFilterToggle = requiredElement<HTMLButtonElement>(
  '#shared-filter-toggle',
);
const sharedFilterIcon = requiredElement<HTMLElement>('#shared-filter-icon');
const sharedResults =
  requiredElement<HTMLTableSectionElement>('#shared-results');
const saveButtons = [
  ...document.querySelectorAll<HTMLButtonElement>('[data-profile]'),
];
const validationTooltip = document.createElement('div');
validationTooltip.className = 'validation-tooltip';
validationTooltip.hidden = true;
validationTooltip.setAttribute('role', 'tooltip');
document.body.append(validationTooltip);
window.rawData = emptyWindowRawData();

function requiredElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) {
    throw new Error(`Missing required element: ${selector}`);
  }
  return element;
}

function isProfile(value: string | undefined): value is Profile {
  return profiles.some((profile) => profile == value);
}

function calculatedResult(test: TestDefinition, rawData: RawData): TestResult {
  const value = test.format(rawData);
  if (value.includes('\n') || [...value].length > 32) {
    throw new Error(`${test.id} produced an invalid table value`);
  }
  return { rawData, value };
}

function hydrateProfile(stored: StoredProfileResults): ProfileResults {
  const results = Object.fromEntries(
    Object.entries(stored.results).flatMap(([testId, storedResult]) => {
      const test = tests.find((candidate) => candidate.id == testId);
      if (!test) {
        return [];
      }
      return [[testId, calculatedResult(test, storedResult.rawData)]];
    }),
  );
  return {
    capturedAt: stored.capturedAt,
    profileName: stored.profileName,
    validation: stored.validation,
    results,
  };
}

function storeProfile(profile: ProfileResults): StoredProfileResults {
  return {
    capturedAt: profile.capturedAt,
    profileName: profile.profileName,
    validation: profile.validation,
    results: Object.fromEntries(
      Object.entries(profile.results).map(([testId, testResult]) => [
        testId,
        { rawData: testResult.rawData },
      ]),
    ),
  };
}

function comparison(
  appState: AppState,
  test: TestDefinition,
): {
  label: string;
  className: string;
} {
  const equality = equalityState(appState, test);
  if (equality == 'waiting') {
    return { label: 'Waiting', className: 'waiting' };
  }
  if (stableControlMismatches(appState, test).length > 0) {
    return { label: 'Different', className: 'fail' };
  }
  if (test.category != 'shared') {
    return equality == 'different'
      ? { label: 'Different', className: 'pass' }
      : { label: 'Same', className: 'neutral' };
  }
  return equality == 'same'
    ? { label: 'Same', className: 'neutral' }
    : { label: 'Different', className: 'fail' };
}

function comparisonRawData(
  appState: AppState,
  test: TestDefinition,
): Partial<Record<StoredProfile, RawData>> {
  const comparedProfiles =
    test.category == 'variableProfile' ? (['A', 'B'] as const) : storedProfiles;
  const rawDataByProfile: Partial<Record<StoredProfile, RawData>> = {};
  for (const profile of comparedProfiles) {
    const rawData = appState.profiles[profile]?.results[test.id]?.rawData;
    if (rawData) {
      rawDataByProfile[profile] = rawData;
    }
  }
  return rawDataByProfile;
}

function showRawDataDiff(appState: AppState, test: TestDefinition): void {
  const messages = compareRawData(comparisonRawData(appState, test));
  const displayedMessages =
    messages.length > 0 ? messages : ['No rawData differences found.'];
  rawDataDiffDialogTitle.textContent = `${test.title} rawData differences`;
  rawDataDiffDialogContent.replaceChildren(
    ...displayedMessages.map((message) => {
      const item = document.createElement('li');
      item.textContent = message;
      return item;
    }),
  );
  rawDataDiffDialog.showModal();
  document.body.classList.add('dialog-open');
}

function comparisonResult(
  appState: AppState,
  test: TestDefinition,
  label: string,
  className: string,
): HTMLElement {
  if (label != 'Different') {
    const badge = document.createElement('span');
    badge.className = `result ${className}`;
    badge.textContent = label;
    return badge;
  }

  const button = document.createElement('button');
  button.type = 'button';
  button.className = `result result-button ${className}`;
  button.textContent = label;
  button.title = `Show rawData differences for ${test.title}`;
  button.setAttribute('aria-label', button.title);
  button.addEventListener('click', () => {
    showRawDataDiff(appState, test);
  });
  return button;
}

function equalityState(
  appState: AppState,
  test: TestDefinition,
): 'waiting' | 'same' | 'different' {
  const a = appState.profiles.A?.results[test.id];
  const b = appState.profiles.B?.results[test.id];
  const c = appState.profiles.C?.results[test.id];

  if (!a || !b || (test.category != 'variableProfile' && !c)) {
    return 'waiting';
  }

  const same =
    test.category == 'shared'
      ? rawDataEqual(a.rawData, b.rawData) &&
        c != undefined &&
        rawDataEqual(a.rawData, c.rawData)
      : rawDataEqual(a.rawData, b.rawData);

  return same ? 'same' : 'different';
}

function stableControlMismatches(
  appState: AppState,
  test: TestDefinition,
): Profile[] {
  if (test.category != 'stableProfile') {
    return [];
  }
  const control = appState.profiles.C?.results[test.id];
  if (!control) {
    return [];
  }
  return (['A', 'B'] as const).filter((profile) => {
    const profileState = appState.profiles[profile];
    const profileResult = profileState?.results[test.id];
    if (
      !profileResult ||
      !testMustMatchControl(test, profileState.validation.hostParams)
    ) {
      return false;
    }
    return !rawDataEqual(profileResult.rawData, control.rawData);
  });
}

function clipboardJson(value: RawData | WindowRawData[Profile]): string {
  return JSON.stringify(value, null, 2);
}

function fallbackCopy(text: string): boolean {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.append(textarea);
  textarea.select();
  try {
    return document.execCommand('copy');
  } finally {
    textarea.remove();
  }
}

async function writeClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  if (!fallbackCopy(text)) {
    throw new Error('Browser rejected clipboard access');
  }
}

function copyButton(
  rawData: RawData | WindowRawData[Profile],
  description: string,
  enabled: boolean,
): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'copy-button';
  button.disabled = !enabled;
  button.title = enabled ? `Copy ${description}` : `No ${description} to copy`;
  button.setAttribute('aria-label', button.title);
  button.append(bootstrapIcon('copy'));
  button.addEventListener('click', async () => {
    try {
      await writeClipboard(clipboardJson(rawData));
      button.classList.add('copied');
      statusElement.textContent = `Copied ${description}.`;
      window.setTimeout(() => {
        button.classList.remove('copied');
      }, 1200);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      statusElement.textContent = `Could not copy ${description}: ${message}`;
    }
  });
  return button;
}

function highlightedJson(rawData: RawData): DocumentFragment {
  const json = clipboardJson(rawData);
  const tokens =
    /("(?:\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*")(?=\s*:)|"(?:\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"|true|false|null|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g;
  const fragment = document.createDocumentFragment();
  let previousEnd = 0;
  for (const match of json.matchAll(tokens)) {
    const index = match.index;
    fragment.append(json.slice(previousEnd, index));
    const token = match[0];
    const span = document.createElement('span');
    if (match[1]) {
      span.className = 'json-key';
    } else if (token.startsWith('"')) {
      span.className = 'json-string';
    } else if (token == 'true' || token == 'false') {
      span.className = 'json-boolean';
    } else if (token == 'null') {
      span.className = 'json-null';
    } else {
      span.className = 'json-number';
    }
    span.textContent = token;
    fragment.append(span);
    previousEnd = index + token.length;
  }
  fragment.append(json.slice(previousEnd));
  return fragment;
}

function showRawData(
  rawData: RawData,
  title: string,
  reasons: readonly string[] = [],
): void {
  rawDataDialogTitle.textContent = title;
  rawDataDialogError.textContent = reasons.join('\n');
  rawDataDialogError.hidden = reasons.length == 0;
  rawDataDialogContent.replaceChildren(highlightedJson(rawData));
  rawDataDialog.showModal();
  document.body.classList.add('dialog-open');
}

function rawDataButton(
  rawData: RawData,
  description: string,
  enabled: boolean,
  reasons: readonly string[],
): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'raw-data-button';
  button.disabled = !enabled;
  button.title = enabled ? `Show ${description}` : `No ${description} to show`;
  button.setAttribute('aria-label', button.title);
  button.append(bootstrapIcon('eye'));
  button.addEventListener('click', () => {
    showRawData(rawData, description, reasons);
  });
  return button;
}

function hostParamsButton(
  hostParams: NonNullable<ProfileResults['validation']['hostParams']>,
  profile: Profile,
): HTMLButtonElement {
  const rawData: RawData = {
    chromeArgs: [...hostParams.chromeArgs],
    ips: { ...hostParams.ips },
  };
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'host-params-button';
  button.title = `Show hostParams for profile ${profile}`;
  button.setAttribute('aria-label', button.title);
  button.append(bootstrapIcon('eye'));
  button.addEventListener('click', () => {
    showRawData(rawData, `hostParams for profile ${profile}`);
  });
  return button;
}

function singleTestButton(
  appState: AppState,
  profile: Profile,
  test: TestDefinition,
): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'single-test-button';
  button.dataset.singleTest = test.id;
  button.title = `Run ${test.title} for profile ${profile}`;
  button.setAttribute('aria-label', button.title);
  button.append(bootstrapIcon('play-circle'));
  button.addEventListener('click', () => {
    void runSingleTest(appState, profile, test);
  });
  return button;
}

function cellActions(
  appState: AppState,
  profile: Profile,
  test: TestDefinition,
  rawData: RawData,
  description: string,
  enabled: boolean,
  reasons: readonly string[],
): HTMLElement {
  const actions = document.createElement('div');
  actions.className = 'cell-actions';
  actions.append(
    singleTestButton(appState, profile, test),
    rawDataButton(rawData, description, enabled, reasons),
    copyButton(rawData, description, enabled),
  );
  return actions;
}

function valueCell(
  appState: AppState,
  profile: Profile,
  test: TestDefinition,
  reasons: readonly string[],
): HTMLElement {
  const content = document.createElement('div');
  content.className = 'cell-content';
  const result = appState.profiles[profile]?.results[test.id];
  const description = `${test.title} rawData for profile ${profile}`;
  if (!result) {
    const empty = document.createElement('span');
    empty.className = 'empty';
    empty.textContent = 'Not saved';
    content.append(
      empty,
      cellActions(appState, profile, test, {}, description, false, reasons),
    );
    return content;
  }
  const value = document.createElement('pre');
  value.className = 'value';
  value.textContent = test.format(result.rawData);
  content.append(
    value,
    cellActions(
      appState,
      profile,
      test,
      result.rawData,
      description,
      true,
      reasons,
    ),
  );
  return content;
}

function cellAssessment(
  appState: AppState,
  profile: Profile,
  test: TestDefinition,
): { classNames: string[]; reasons: string[] } {
  const profileResults = appState.profiles[profile]?.results;
  const result = profileResults?.[test.id];
  if (!result || !profileResults) {
    return { classNames: [], reasons: [] };
  }

  const classNames: string[] = [];
  const reasons: string[] = [];
  const validation = test.validate(result.rawData, {
    profile,
    results: profileResults,
    hostParams: appState.profiles[profile]?.validation.hostParams ?? null,
  });
  if (!validation.valid) {
    classNames.push('invalid');
    reasons.push(validation.reason ?? 'Invalid Chrome value');
  }
  if (validation.note) {
    classNames.push('info');
    reasons.push(validation.note);
  }

  if (stableControlMismatches(appState, test).includes(profile)) {
    classNames.push('invalid');
    reasons.push('Unspoofed stable profile rawData must match profile C');
  }

  const equality = equalityState(appState, test);
  if (
    test.category != 'shared' &&
    equality == 'different' &&
    (profile == 'A' || profile == 'B')
  ) {
    classNames.push('profile-different');
  }
  if (profile != 'D' && test.category == 'shared' && equality == 'different') {
    classNames.push('shared-mismatch');
    reasons.push('A, B, and C must have the same rawData');
  }
  return { classNames, reasons };
}

enum TooltipIndicatorKind {
  Info = 'info',
  Warning = 'warning',
}

function tooltipIndicatorKind(
  classNames: readonly string[],
): TooltipIndicatorKind {
  if (
    classNames.includes('invalid') ||
    classNames.includes('shared-mismatch')
  ) {
    return TooltipIndicatorKind.Warning;
  }
  if (classNames.includes('info')) {
    return TooltipIndicatorKind.Info;
  }
  throw new Error('Tooltip indicator kind is missing');
}

function tooltipIndicator(
  reasons: readonly string[],
  kind: TooltipIndicatorKind,
): HTMLElement {
  const indicator = document.createElement('span');
  indicator.classList.add('tooltip-indicator', kind);
  indicator.tabIndex = 0;
  indicator.setAttribute('aria-label', reasons.join('. '));
  let icon: string;
  switch (kind) {
    case TooltipIndicatorKind.Info: {
      icon = 'question-circle';
      break;
    }
    case TooltipIndicatorKind.Warning: {
      icon = 'exclamation-circle';
      break;
    }
  }
  indicator.append(bootstrapIcon(icon));
  const show = (): void => {
    showValidationTooltip(indicator, reasons);
  };
  indicator.addEventListener('mouseenter', show);
  indicator.addEventListener('focus', show);
  indicator.addEventListener('mouseleave', hideValidationTooltip);
  indicator.addEventListener('blur', hideValidationTooltip);
  return indicator;
}

function bootstrapIcon(name: string): HTMLElement {
  const icon = document.createElement('i');
  icon.className = `bi bi-${name}`;
  icon.setAttribute('aria-hidden', 'true');
  return icon;
}

function showValidationTooltip(
  indicator: HTMLElement,
  reasons: readonly string[],
): void {
  const margin = 8;
  const gap = 8;
  const anchor = indicator.getBoundingClientRect();
  validationTooltip.textContent = reasons.join('\n');
  validationTooltip.hidden = false;
  validationTooltip.style.maxWidth = `${Math.max(
    1,
    Math.min(360, window.innerWidth - margin * 2),
  )}px`;

  const tooltipWidth = validationTooltip.offsetWidth;
  const tooltipHeight = validationTooltip.offsetHeight;
  const maximumLeft = Math.max(
    margin,
    window.innerWidth - tooltipWidth - margin,
  );
  const left = Math.min(
    maximumLeft,
    Math.max(margin, anchor.right - tooltipWidth),
  );
  const below = anchor.bottom + gap;
  const above = anchor.top - tooltipHeight - gap;
  const top =
    below + tooltipHeight <= window.innerHeight - margin
      ? below
      : Math.max(margin, above);
  validationTooltip.style.left = `${left}px`;
  validationTooltip.style.top = `${top}px`;
}

function hideValidationTooltip(): void {
  validationTooltip.hidden = true;
}

window.addEventListener('resize', hideValidationTooltip);
window.addEventListener('scroll', hideValidationTooltip, true);
rawDataDialogClose.addEventListener('click', () => {
  rawDataDialog.close();
});
rawDataDialogCopy.addEventListener('click', async () => {
  try {
    await writeClipboard(rawDataDialogContent.textContent ?? '');
    rawDataDialogCopy.classList.remove('copy-failed');
    rawDataDialogCopy.classList.add('copied');
    rawDataDialogCopy.replaceChildren(bootstrapIcon('check-lg'), ' Copied');
  } catch (error) {
    rawDataDialogCopy.classList.remove('copied');
    rawDataDialogCopy.classList.add('copy-failed');
    rawDataDialogCopy.replaceChildren(
      bootstrapIcon('exclamation-circle'),
      ' Copy failed',
    );
    rawDataDialogCopy.title =
      error instanceof Error ? error.message : String(error);
  }
  window.setTimeout(() => {
    rawDataDialogCopy.classList.remove('copied', 'copy-failed');
    rawDataDialogCopy.replaceChildren(bootstrapIcon('copy'), ' Copy JSON');
    rawDataDialogCopy.removeAttribute('title');
  }, 1200);
});
rawDataDialog.addEventListener('close', () => {
  document.body.classList.remove('dialog-open');
});
rawDataDialog.addEventListener('click', (event) => {
  if (event.target === rawDataDialog) {
    rawDataDialog.close();
  }
});
rawDataDiffDialogClose.addEventListener('click', () => {
  rawDataDiffDialog.close();
});
rawDataDiffDialog.addEventListener('close', () => {
  document.body.classList.remove('dialog-open');
});
rawDataDiffDialog.addEventListener('click', (event) => {
  if (event.target == rawDataDiffDialog) {
    rawDataDiffDialog.close();
  }
});

const categoryBodyIds: Record<TestCategory, string> = {
  shared: 'shared-results',
  stableProfile: 'stable-profile-results',
  variableProfile: 'variable-profile-results',
};

function renderTable(appState: AppState, category: TestCategory): void {
  const body = requiredElement<HTMLTableSectionElement>(
    `#${categoryBodyIds[category]}`,
  );
  body.replaceChildren(
    ...tests
      .filter((test) => test.category == category)
      .map((test) => {
        const result = comparison(appState, test);
        const displayedProfiles = profiles;
        const assessments = Object.fromEntries(
          displayedProfiles.map((profile) => [
            profile,
            cellAssessment(appState, profile, test),
          ]),
        ) as Record<Profile, ReturnType<typeof cellAssessment>>;
        const hasInvalidCell = displayedProfiles.some((profile) =>
          assessments[profile].classNames.includes('invalid'),
        );
        if (hasInvalidCell) {
          for (const profile of displayedProfiles) {
            assessments[profile].classNames = assessments[
              profile
            ].classNames.filter(
              (className) => className != 'profile-different',
            );
          }
        }
        const row = document.createElement('tr');
        if (
          category == 'shared' &&
          equalityState(appState, test) != 'different' &&
          !hasInvalidCell
        ) {
          row.classList.add('shared-filter-hidden');
        }
        const titleCell = document.createElement('td');
        titleCell.textContent = test.title;
        const comparisonCell = document.createElement('td');
        comparisonCell.append(
          comparisonResult(appState, test, result.label, result.className),
        );
        row.append(
          titleCell,
          ...displayedProfiles.map((profile) => {
            const cell = document.createElement('td');
            if (profile == 'D') {
              cell.dataset.localProfileColumn = '';
              cell.hidden = appState.profiles.D == null;
            }
            const assessment = assessments[profile];
            cell.classList.add(...assessment.classNames);
            cell.append(valueCell(appState, profile, test, assessment.reasons));
            if (assessment.reasons.length > 0) {
              cell.append(
                tooltipIndicator(
                  assessment.reasons,
                  tooltipIndicatorKind(assessment.classNames),
                ),
              );
            }
            return cell;
          }),
          comparisonCell,
        );
        return row;
      }),
  );
}

function syncWindowRawData(appState: AppState): void {
  window.rawData = emptyWindowRawData();
  for (const profile of profiles) {
    window.rawData[profile] = profileRawData(appState.profiles[profile]);
  }
}

function renderProfileHeadings(appState: AppState): void {
  for (const profile of profiles) {
    const profileResults = appState.profiles[profile];
    const identityRawData = profileResults?.results.browserIdentity?.rawData;
    const identity =
      identityRawData && typeof identityRawData.error != 'string'
        ? browserIdentity(identityRawData)
        : null;
    const browserLabel = identity
      ? `${identity.name} ${identity.version}`.trim()
      : null;
    const hasRawData = profileResults != null;
    for (const element of document.querySelectorAll<HTMLElement>(
      `[data-profile-heading="${profile}"]`,
    )) {
      if (profile == 'D') {
        element.hidden = profileResults == null;
      }
      const content = document.createElement('div');
      content.className = 'profile-heading';
      const label = document.createElement('span');
      label.className = 'profile-heading-label';
      const browserLine = document.createElement('span');
      browserLine.className = 'profile-browser';
      browserLine.textContent = browserLabel ?? profile;
      label.append(browserLine);
      if (profileResults?.profileName) {
        const profileLineContainer = document.createElement('span');
        profileLineContainer.className = 'profile-name-row';
        const profileLine = document.createElement('span');
        profileLine.className = 'profile-name';
        profileLine.textContent = profileResults.profileName;
        profileLineContainer.append(profileLine);
        const hostParams = profileResults.validation.hostParams;
        if (hostParams) {
          profileLineContainer.append(hostParamsButton(hostParams, profile));
        }
        label.append(profileLineContainer);
      }
      content.append(
        label,
        copyButton(
          profileRawData(profileResults),
          `all rawData for profile ${profile}`,
          hasRawData,
        ),
      );
      element.replaceChildren(content);
    }
  }
}

function render(appState: AppState): void {
  renderProfileHeadings(appState);
  renderTable(appState, 'stableProfile');
  renderTable(appState, 'variableProfile');
  renderTable(appState, 'shared');
}

async function load(appState: AppState): Promise<void> {
  const response = await fetch('/results.json', { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Load failed (${response.status})`);
  }
  const loaded = (await response.json()) as ResultsDocument;
  appState.updatedAt = loaded.updatedAt;
  for (const profile of storedProfiles) {
    const stored = loaded.profiles[profile];
    appState.profiles[profile] = stored ? hydrateProfile(stored) : null;
  }
  syncWindowRawData(appState);
  render(appState);
  statusElement.textContent = appState.updatedAt
    ? `Loaded. Last save: ${new Date(appState.updatedAt).toLocaleString()}`
    : 'Ready. No saved runs yet.';
}

function errorResult(test: TestDefinition, error: unknown): TestResult {
  const message = error instanceof Error ? error.message : String(error);
  return calculatedResult(test, { error: message });
}

async function showTestProgress(
  testName: string,
  completed: number,
  total: number,
): Promise<void> {
  progressElement.hidden = false;
  progressBar.max = total;
  progressBar.value = completed;
  progressName.textContent = testName;
  progressCount.textContent = `${completed} / ${total}`;
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

function setExecutionDisabled(disabled: boolean): void {
  saveButtons.forEach((button) => {
    button.disabled = disabled;
  });
  document
    .querySelectorAll<HTMLButtonElement>('.single-test-button')
    .forEach((button) => {
      button.disabled = disabled;
    });
}

async function persistProfile(
  appState: AppState,
  profile: StoredProfile,
  profileResults: ProfileResults,
): Promise<string> {
  const updatedAt = new Date().toISOString();

  const request: ProfileSaveRequest = {
    profile,
    updatedAt,
    profileResults: storeProfile(profileResults),
  };

  const response = await fetch('/results.json', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw Error(`Save failed (${response.status})`);
  }

  appState.profiles[profile] = profileResults;
  appState.updatedAt = updatedAt;
  syncWindowRawData(appState);
  render(appState);
  return updatedAt;
}

async function runSingleTest(
  appState: AppState,
  profile: Profile,
  test: TestDefinition,
): Promise<void> {
  setExecutionDisabled(true);
  statusElement.textContent = `Running ${test.title} for ${profile}…`;
  try {
    const results = { ...(appState.profiles[profile]?.results ?? {}) };
    await showTestProgress(test.title, 0, 1);
    try {
      const testResult = await test.run({ results });
      results[test.id] = calculatedResult(test, testResult.rawData);
    } catch (error) {
      results[test.id] = errorResult(test, error);
    }
    await showTestProgress(
      profile == 'D' ? 'Updating local result…' : 'Saving result…',
      1,
      1,
    );
    const hostParams = currentHostParams();
    const profileResults: ProfileResults = {
      capturedAt: new Date().toISOString(),
      profileName: profileNameFromChromeArgs(hostParams?.chromeArgs ?? null),
      validation: { hostParams },
      results,
    };
    if (profile == 'D') {
      appState.profiles.D = profileResults;
      syncWindowRawData(appState);
      render(appState);
      statusElement.textContent = `${test.title} captured in local profile D.`;
    } else {
      const updatedAt = await persistProfile(appState, profile, profileResults);
      statusElement.textContent = `${test.title} for profile ${profile} saved at ${new Date(updatedAt).toLocaleString()}.`;
    }
  } catch (error) {
    statusElement.textContent =
      error instanceof Error ? error.message : String(error);
  } finally {
    progressElement.hidden = true;
    setExecutionDisabled(false);
  }
}

async function save(appState: AppState, profile: Profile): Promise<void> {
  setExecutionDisabled(true);
  statusElement.textContent = `Running tests for ${profile}…`;
  try {
    const hostParams = currentHostParams();
    const results: Record<string, TestResult> = {};

    for (const [index, test] of tests.entries()) {
      await showTestProgress(test.title, index, tests.length);
      try {
        const testResult = await test.run({ results });
        results[test.id] = calculatedResult(test, testResult.rawData);
      } catch (error) {
        results[test.id] = errorResult(test, error);
      }
    }

    await showTestProgress(
      profile == 'D' ? 'Preparing local results…' : 'Saving results…',
      tests.length,
      tests.length,
    );

    const profileResults = {
      capturedAt: new Date().toISOString(),
      profileName: profileNameFromChromeArgs(hostParams?.chromeArgs ?? null),
      validation: { hostParams },
      results,
    };

    if (profile == 'D') {
      appState.profiles.D = profileResults;
      syncWindowRawData(appState);
      render(appState);
      statusElement.textContent =
        'Current browser captured in local profile D.';
    } else {
      const updatedAt = await persistProfile(appState, profile, profileResults);
      statusElement.textContent = `Profile ${profile} saved at ${new Date(updatedAt).toLocaleString()}.`;
    }
    progressName.textContent = 'Completed';
  } catch (error) {
    statusElement.textContent =
      error instanceof Error ? error.message : String(error);
  } finally {
    progressElement.hidden = true;
    setExecutionDisabled(false);
  }
}

function main(): void {
  const appState = emptyState();
  sharedFilterToggle.title = 'Show only different or invalid shared values';
  sharedFilterToggle.setAttribute(
    'aria-label',
    'Show only different or invalid shared values',
  );
  sharedFilterToggle.addEventListener('click', () => {
    const hideMatching = sharedResults.classList.toggle('hide-matching');
    sharedFilterToggle.setAttribute('aria-pressed', String(hideMatching));
    sharedFilterToggle.title = hideMatching
      ? 'Show all shared values'
      : 'Show only different or invalid shared values';
    sharedFilterToggle.setAttribute('aria-label', sharedFilterToggle.title);
    sharedFilterIcon.className = hideMatching
      ? 'bi bi-chevron-down'
      : 'bi bi-chevron-up';
  });
  saveButtons.forEach((button) =>
    button.addEventListener('click', () => {
      if (isProfile(button.dataset.profile)) {
        void save(appState, button.dataset.profile);
      }
    }),
  );
  void load(appState).catch((error: unknown) => {
    statusElement.textContent =
      error instanceof Error ? error.message : String(error);
  });
}

main();
